import FormFieldViewText from "components/FormFields/ViewText";
import MetaInfoListItem from "components/MetaInfoList/Item";
import { Spinner } from "components/Spinner";
import TagsList from "components/TagsList";
import Box from "ds/components/Box";
import Feedback from "ds/components/Feedback";
import FormField from "ds/components/Form/Field";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { CLOUD_INTEGRATIONS } from "shared/CloudIntegration/types";
import { getCloudIntegrationUrl } from "shared/CloudIntegration/urls";
import useScanProviderSchema from "views/Account/NewScanSchedule/hooks/useScanProviderSchema";
import { SCAN_PROVIDER_LABEL } from "views/Account/Scans/components/Table/constants";

import FieldsGroup from "../FieldsGroup";
import { getRegionLabels, groupProviderConfigs } from "../helpers";
import styles from "../styles.module.css";
import { ScanDetailsDrawerItem } from "../types";

type AttachProvidersTabProps = {
  providerConfigs: ScanDetailsDrawerItem["providerConfigs"];
};

const AttachProvidersTab = ({ providerConfigs }: AttachProvidersTabProps) => {
  const { regions: regionOptions, loading, error } = useScanProviderSchema();
  const providerGroups = groupProviderConfigs(providerConfigs);

  if (loading) {
    return (
      <Box grow="1" justify="center" align="center">
        <Icon src={Spinner} size="x-large" />
      </Box>
    );
  }

  if (providerGroups.length === 0) {
    return (
      <FieldsGroup>
        <FormFieldViewText label="Attached providers" value={undefined} noMargin />
      </FieldsGroup>
    );
  }

  return (
    <FieldsGroup>
      {error && (
        <Feedback type="banner" variant="danger">
          Could not load region labels.
        </Feedback>
      )}

      {providerGroups.map((providerGroup, index) => {
        const regionLabels = getRegionLabels(providerGroup.regions, regionOptions);

        return (
          <Box
            key={`${providerGroup.type}-${providerGroup.version}-${providerGroup.integration}`}
            direction="column"
            gap="large"
            fullWidth
            className={index > 0 ? styles.providerGroup : undefined}
          >
            {providerGroups.length > 1 && (
              <Typography tag="h3" variant="p-t6">
                Provider {index + 1}
              </Typography>
            )}

            <FormFieldViewText
              label="Provider"
              value={SCAN_PROVIDER_LABEL[providerGroup.type]}
              noMargin
            />
            <FormFieldViewText label="Provider version" value={providerGroup.version} noMargin />

            {providerGroup.integrationName && (
              <FormField label="Integration" noMargin>
                <MetaInfoListItem
                  linkText={providerGroup.integrationName}
                  href={getCloudIntegrationUrl(CLOUD_INTEGRATIONS.AWS, providerGroup.integration)}
                />
              </FormField>
            )}

            <FormField label="Regions" noMargin>
              {regionLabels.length > 0 ? (
                <TagsList tags={regionLabels} alwaysExpanded />
              ) : (
                <Typography tag="span" variant="p-body3" color="disabled">
                  None
                </Typography>
              )}
            </FormField>
          </Box>
        );
      })}
    </FieldsGroup>
  );
};

export default AttachProvidersTab;
